import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { calcularSubtotal, calcularTotalPedido } from "../../lib/calculo";
import { iconoCategoria, iconoChevron, iconoRevertir, iconoMas, iconoTacho, iconoBuscar } from "../iconos";
import { montarBuscador, marcarFila } from "../buscador";
import { marcaEmpresa } from "../remito";
import { TURNOS, nombreTurno } from "../../lib/types";
import type {
  Cliente,
  Empresa,
  Sesion,
  SugeridoRespuesta,
  PreparaPedido,
  PreparaItem,
  Pedido,
  PedidoLinea,
  Turno,
} from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

interface EstadoPedido {
  prepara: PreparaPedido | null;
  cantidades: Map<number, number>;
  overrides: Map<number, number>; // precio manual por producto
  ocultos: Set<number>; // sacados de este pedido
  agregados: Set<number>; // traidos por busqueda (aunque no esten en la lista)
  verSoloCargados: boolean;
}

// Pedido en curso persistido en el navegador (sobrevive navegacion y recarga).
const KEY_EN_CURSO = "pedido_en_curso";
interface EstadoGuardado {
  cliente_id: number;
  empresa_id?: number;
  fecha: string;
  descuento: number;
  cantidades: Array<[number, number]>;
  overrides: Array<[number, number]>;
  ocultos: number[];
  agregados: number[];
}
function leerEstadoGuardado(): EstadoGuardado | null {
  try {
    const s = localStorage.getItem(KEY_EN_CURSO);
    return s ? (JSON.parse(s) as EstadoGuardado) : null;
  } catch {
    return null;
  }
}

export async function renderPedido(contenedor: HTMLElement): Promise<void> {
  const st: EstadoPedido = {
    prepara: null,
    cantidades: new Map(),
    overrides: new Map(),
    ocultos: new Set(),
    agregados: new Set(),
    verSoloCargados: false,
  };

  const claveCat = (catId: number): string => `pedido_cat_abierta_${catId}`;
  const catAbierta = (catId: number): boolean => localStorage.getItem(claveCat(catId)) === "1";

  contenedor.innerHTML = `
    <div id="contenido" class="no-print space-y-4 pb-24">
      <div class="tarjeta grid grid-cols-1 gap-3 sm:grid-cols-6">
        <label class="sm:col-span-2"><span class="etiqueta">Cliente</span>
          <select id="cliente" class="campo"><option value="">Cargando...</option></select></label>
        <label class="sm:col-span-2"><span class="etiqueta">Empresa emisora</span>
          <select id="empresa" class="campo"><option value="">Elegir empresa...</option></select></label>
        <label><span class="etiqueta">Fecha de entrega</span>
          <input id="fecha" type="date" class="campo" /></label>
        <div><span class="etiqueta">Turno</span>
          <div id="turnos" class="flex gap-1">
            ${TURNOS.map(
              (t, i) =>
                `<button type="button" data-turno="${t.valor}"
                  class="btn-sec turno-btn flex-1 px-2 py-2 text-sm${i === 0 ? " turno-activo" : ""}">${t.nombre}</button>`,
            ).join("")}
          </div>
        </div>
        <label><span class="etiqueta">Descuento ($)</span>
          <input id="descuento" type="number" min="0" value="0" class="campo" /></label>
        <div id="buscador" class="hidden sm:col-span-6"></div>
        <div class="sm:col-span-6">
          <button id="sugerir" class="btn-sec text-sm" disabled>Sugerir cantidades (último pedido)</button>
        </div>
      </div>
      <div id="grilla" class="space-y-3"></div>
    </div>

    <div id="comanda" class="solo-print"></div>
    <dialog id="dlgAdd"></dialog>

    <div id="barra" class="no-print fixed inset-x-0 bottom-0 z-20 hidden border-t border-crema-200 bg-papel/95 backdrop-blur">
      <div class="mx-auto flex max-w-5xl flex-wrap items-center gap-2 p-3">
        <div class="flex-1">
          <div class="etiqueta mb-0">Total estimado</div>
          <div id="total" class="text-2xl font-bold text-masa-900">$ 0</div>
        </div>
        <button id="toggle" class="btn-sec">Ver solo cargados</button>
        <button id="limpiar" class="btn-sec">Limpiar</button>
        <button id="confirmar" class="btn-primario">Confirmar pedido</button>
      </div>
    </div>`;

  const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
  const selEmpresa = contenedor.querySelector("#empresa") as HTMLSelectElement;
  const inpFecha = contenedor.querySelector("#fecha") as HTMLInputElement;
  const inpDesc = contenedor.querySelector("#descuento") as HTMLInputElement;

  // Turno de entrega: obligatorio, arranca en Manana (lo habitual).
  let turno: Turno = "MANANA";
  contenedor.querySelector("#turnos")?.addEventListener("click", (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>("[data-turno]");
    if (!btn) return;
    turno = btn.dataset.turno as Turno;
    contenedor
      .querySelectorAll<HTMLElement>(".turno-btn")
      .forEach((b) => b.classList.toggle("turno-activo", b.dataset.turno === turno));
  });
  const btnSugerir = contenedor.querySelector("#sugerir") as HTMLButtonElement;
  const grilla = contenedor.querySelector("#grilla") as HTMLElement;
  const cajaBuscador = contenedor.querySelector("#buscador") as HTMLElement;
  const barra = contenedor.querySelector("#barra") as HTMLElement;
  const contenido = contenedor.querySelector("#contenido") as HTMLElement;

  // En el celular la barra fija se envuelve en varias filas y tapaba el ultimo
  // articulo. Reservamos abajo el alto REAL de la barra + 16px de aire.
  function ajustarEspacioBarra(): void {
    contenido.style.paddingBottom = barra.classList.contains("hidden")
      ? "0px"
      : `${barra.offsetHeight + 16}px`;
  }
  if ("ResizeObserver" in window) {
    new ResizeObserver(ajustarEspacioBarra).observe(barra);
  }
  window.addEventListener("resize", ajustarEspacioBarra);
  const elTotal = contenedor.querySelector("#total") as HTMLElement;
  const dlgAdd = contenedor.querySelector("#dlgAdd") as HTMLDialogElement;

  let empresas: Empresa[] = [];
  let sesion: Sesion | null = null;
  try {
    const [clientes, meta, ses] = await Promise.all([
      api.get<Cliente[]>("/api/clientes?activo=1"),
      api.get<{ empresas: Empresa[] }>("/api/meta"),
      api.get<Sesion>("/api/sesion"),
    ]);
    sesion = ses;
    empresas = meta.empresas;
    selCliente.innerHTML =
      `<option value="">Elegir cliente...</option>` +
      clientes.map((c) => `<option value="${c.id}">${esc(c.codigo)} — ${esc(c.nombre)}</option>`).join("");
    selEmpresa.innerHTML =
      `<option value="">Elegir empresa...</option>` +
      empresas.map((e) => `<option value="${e.id}">${esc(e.nombre)}</option>`).join("");
  } catch {
    selCliente.innerHTML = `<option value="">Error al cargar clientes</option>`;
  }
  // El ajuste manual de precio por linea es atribucion del admin; editar la
  // lista del cliente requiere el permiso "listas".
  const puedeAjustarPrecio = sesion?.es_admin === true;
  const puedeEditarListas = sesion?.es_admin === true || (sesion?.permisos ?? []).includes("listas");

  const precioEfectivo = (it: PreparaItem): number =>
    st.overrides.has(it.producto.id) ? (st.overrides.get(it.producto.id) as number) : it.precio;

  // Un producto se muestra si no fue sacado y (tiene precio en la lista o fue agregado a mano).
  const visible = (it: PreparaItem): boolean =>
    !st.ocultos.has(it.producto.id) && (it.precio > 0 || st.agregados.has(it.producto.id));

  function guardarEstado(): void {
    if (!st.prepara) { localStorage.removeItem(KEY_EN_CURSO); return; }
    const g: EstadoGuardado = {
      cliente_id: st.prepara.cliente.id,
      empresa_id: Number(selEmpresa.value) || 0,
      fecha: inpFecha.value,
      descuento: Number(inpDesc.value) || 0,
      cantidades: [...st.cantidades],
      overrides: [...st.overrides],
      ocultos: [...st.ocultos],
      agregados: [...st.agregados],
    };
    try { localStorage.setItem(KEY_EN_CURSO, JSON.stringify(g)); } catch { /* storage lleno */ }
  }

  function recalcularTotal(): number {
    const items = st.prepara?.items ?? [];
    const lineas = items
      .filter((it) => visible(it) && (st.cantidades.get(it.producto.id) ?? 0) > 0)
      .map((it) => ({ subtotal: calcularSubtotal(precioEfectivo(it), st.cantidades.get(it.producto.id) ?? 0) }));
    const total = calcularTotalPedido(lineas, Number(inpDesc.value) || 0);
    elTotal.textContent = formatearPesos(total);
    guardarEstado();
    return total;
  }

  function pintarGrilla(): void {
    if (!st.prepara) {
      grilla.innerHTML = "";
      barra.classList.add("hidden");
      cajaBuscador.classList.add("hidden");
      ajustarEspacioBarra();
      return;
    }
    barra.classList.remove("hidden");
    cajaBuscador.classList.remove("hidden");

    const porCat = new Map<number, PreparaItem[]>();
    for (const it of st.prepara.items) {
      if (!visible(it)) continue;
      if (st.verSoloCargados && (st.cantidades.get(it.producto.id) ?? 0) <= 0) continue;
      const arr = porCat.get(it.producto.categoria_id) ?? [];
      arr.push(it);
      porCat.set(it.producto.categoria_id, arr);
    }
    const nombreCat = new Map(st.prepara.categorias.map((c) => [c.id, c.nombre]));
    const orden = st.prepara.categorias.map((c) => c.id);

    const bloques: string[] = [];
    for (const catId of orden) {
      const items = porCat.get(catId);
      if (!items || items.length === 0) continue;
      const nom = nombreCat.get(catId) ?? "Otros";
      const filas = items
        .map((it) => {
          const pid = it.producto.id;
          const cant = st.cantidades.get(pid) ?? 0;
          const precio = precioEfectivo(it);
          const conOverride = st.overrides.has(pid);
          const sub = calcularSubtotal(precio, cant);
          return `
          <tr data-fila="${pid}" class="border-t border-crema-100">
            <td class="py-2 pr-2">
              <div class="font-medium">${esc(it.producto.descripcion)}</div>
              <div class="text-xs text-masa-400">#${esc(it.producto.codigo)} · x${esc(it.producto.empaque)}</div>
            </td>
            <td class="py-2 pr-2">
              ${puedeAjustarPrecio
                ? `<div class="flex items-center justify-end gap-1">
                <div class="relative">
                  <span class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-masa-400">$</span>
                  <input class="precio-input ${conOverride ? "precio-override" : ""}" type="number" min="0" step="1"
                         inputmode="numeric" data-precio="${pid}" value="${precio || ""}" placeholder="0" />
                </div>
                <button class="chip-accion ${conOverride ? "" : "hidden"}" data-revert="${pid}" title="Volver al precio de lista">${iconoRevertir}</button>
              </div>`
                : `<div class="text-right font-medium tabular-nums">${formatearPesos(precio)}</div>`}
            </td>
            <td class="py-2 pr-2">
              <div class="flex items-center justify-end gap-1">
                <button class="stepper-btn" data-step="-1" data-pid="${pid}">−</button>
                <input class="cant" type="number" min="0" step="any" inputmode="decimal" data-pid="${pid}" value="${cant || ""}" placeholder="0" />
                <button class="stepper-btn" data-step="1" data-pid="${pid}">+</button>
              </div>
            </td>
            <td class="py-2 pr-1 text-right font-medium tabular-nums" data-sub="${pid}">${cant > 0 ? formatearPesos(sub) : ""}</td>
            <td class="py-2 text-right"><button class="chip-accion" data-sacar="${pid}" title="Sacar del pedido">${iconoTacho}</button></td>
          </tr>`;
        })
        .join("");
      bloques.push(`
        <details ${catAbierta(catId) ? "open" : ""} data-cat="${catId}" class="tarjeta p-0">
          <summary class="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
            <span class="text-masa-500">${iconoCategoria(nom)}</span>
            <span class="flex-1">${esc(nom)}</span>
            <span class="chevron text-masa-400">${iconoChevron}</span>
          </summary>
          <div class="overflow-x-auto px-4 pb-3">
            <table class="w-full text-sm">
              <thead class="text-[11px] uppercase tracking-wider text-masa-400">
                <tr><th class="text-left font-normal">Producto</th><th class="text-right font-normal">Precio</th><th class="text-right font-normal">Cantidad</th><th class="text-right font-normal">Subtotal</th><th></th></tr>
              </thead>
              <tbody>${filas}</tbody>
            </table>
            <button class="btn-sec mt-2 w-full justify-center text-sm" data-add-art="${catId}">${iconoMas} Agregar / quitar artículos</button>
          </div>
        </details>`);
    }
    grilla.innerHTML =
      bloques.join("") ||
      `<div class="tarjeta text-center text-masa-400">Sin productos cargados para este cliente.</div>`;

    grilla.querySelectorAll<HTMLDetailsElement>("details[data-cat]").forEach((d) => {
      d.addEventListener("toggle", () => {
        const catId = d.dataset.cat as string;
        if (d.open) localStorage.setItem(`pedido_cat_abierta_${catId}`, "1");
        else localStorage.removeItem(`pedido_cat_abierta_${catId}`);
      });
    });
    recalcularTotal();
    ajustarEspacioBarra();
  }

  // ------- Buscador de articulos -------
  // Busca sobre TODO el catalogo del cliente, no solo sobre lo que se ve: si el
  // articulo estaba sacado del pedido, sin precio de lista o escondido por "ver
  // solo cargados", elegirlo lo devuelve a la grilla en lugar de no hacer nada.
  function irAlProducto(pid: number): void {
    const it = st.prepara?.items.find((i) => i.producto.id === pid);
    if (!it) return;
    let repintar = false;
    if (st.ocultos.has(pid)) {
      st.ocultos.delete(pid);
      repintar = true;
    }
    if (it.precio <= 0 && !st.agregados.has(pid)) {
      st.agregados.add(pid);
      repintar = true;
    }
    if (st.verSoloCargados && (st.cantidades.get(pid) ?? 0) <= 0) {
      st.verSoloCargados = false;
      repintar = true;
    }
    if (repintar) pintarGrilla();
    marcarFila(grilla, pid, it.producto.categoria_id);
  }

  montarBuscador(cajaBuscador, {
    items: () => {
      if (!st.prepara) return [];
      const nombreCat = new Map(st.prepara.categorias.map((c) => [c.id, c.nombre]));
      return st.prepara.items.map((it) => {
        const pid = it.producto.id;
        const cant = st.cantidades.get(pid) ?? 0;
        const precio = precioEfectivo(it);
        return {
          id: pid,
          descripcion: it.producto.descripcion,
          codigo: String(it.producto.codigo),
          categoria_id: it.producto.categoria_id,
          categoria: nombreCat.get(it.producto.categoria_id) ?? "",
          detalle: cant > 0 ? `${cant} cargados` : precio > 0 ? formatearPesos(precio) : "sin precio",
        };
      });
    },
    alElegir: (item) => irAlProducto(item.id),
  });

  function actualizarSub(pid: number): void {
    const it = st.prepara?.items.find((i) => i.producto.id === pid);
    const celda = grilla.querySelector(`[data-sub="${pid}"]`);
    const cant = st.cantidades.get(pid) ?? 0;
    if (it && celda) celda.textContent = cant > 0 ? formatearPesos(calcularSubtotal(precioEfectivo(it), cant)) : "";
    recalcularTotal();
  }

  // ------- Agregar / quitar articulos de una categoria (casillas) -------
  function abrirArticulos(catId: number): void {
    if (!st.prepara) return;
    const items = st.prepara.items.filter((it) => it.producto.categoria_id === catId);
    const nomCat = st.prepara.categorias.find((c) => c.id === catId)?.nombre ?? "";
    // Estado de trabajo: que queda tildado y que precio manual tienen los nuevos.
    const marcados = new Set(items.filter((it) => visible(it)).map((it) => it.producto.id));
    const preciosNuevos = new Map<number, number>();

    dlgAdd.innerHTML = `
      <div class="tarjeta w-[32rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Artículos de ${esc(nomCat)}</h2>
        <p class="mb-3 text-sm text-masa-500">Tilda lo que va en este pedido y destilda lo que no. ${puedeAjustarPrecio ? "A los que no tienen precio de lista podes ponerles precio." : "Los articulos usan el precio de la lista."}</p>
        <div class="relative mb-2">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-masa-400">${iconoBuscar}</span>
          <input id="a_buscar" class="campo pl-10" placeholder="Buscar por nombre o código..." />
        </div>
        <div id="a_lista" class="mb-3 max-h-72 overflow-y-auto rounded-lg border border-crema-200"></div>
        ${puedeAjustarPrecio && puedeEditarListas
          ? `<label class="flex items-center gap-2">
            <input id="a_lista_chk" type="checkbox" class="h-4 w-4 accent-masa-600" />
            <span class="text-sm">Sumar los precios nuevos a la lista de ${esc(st.prepara.cliente.nombre)}</span></label>`
          : ""}
        <p id="a_err" class="mt-1 text-sm text-red-600"></p>
        <div class="mt-3 flex justify-end gap-2">
          <button id="a_cancelar" class="btn-sec">Cancelar</button>
          <button id="a_aplicar" class="btn-primario">Aplicar</button>
        </div>
      </div>`;

    const listaEl = dlgAdd.querySelector("#a_lista") as HTMLElement;
    const buscarEl = dlgAdd.querySelector("#a_buscar") as HTMLInputElement;
    const aErr = dlgAdd.querySelector("#a_err") as HTMLElement;

    function filaItem(it: PreparaItem): string {
      const pid = it.producto.id;
      const tildado = marcados.has(pid);
      const cant = st.cantidades.get(pid) ?? 0;
      const sinPrecioLista = it.precio <= 0 && !st.overrides.has(pid);
      const precioTxt = st.overrides.has(pid)
        ? formatearPesos(st.overrides.get(pid) as number)
        : it.precio > 0
          ? formatearPesos(it.precio)
          : "";
      return `<label class="flex w-full cursor-pointer items-center gap-2 border-b border-crema-100 px-3 py-2 text-sm hover:bg-crema-50">
        <input type="checkbox" data-chk="${pid}" class="h-4 w-4 accent-masa-600" ${tildado ? "checked" : ""} />
        <span class="flex-1">${esc(it.producto.descripcion)}
          <span class="text-xs text-masa-400">#${esc(it.producto.codigo)} · x${esc(it.producto.empaque)}</span>
          ${cant > 0 ? `<span class="ml-1 rounded-full bg-masa-100 px-1.5 text-xs font-semibold text-masa-700">${cant} cargados</span>` : ""}
        </span>
        ${sinPrecioLista && puedeAjustarPrecio
          ? `<span class="relative ${tildado ? "" : "hidden"}" data-precio-wrap="${pid}">
              <span class="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-masa-400">$</span>
              <input type="number" min="0" data-precio-nuevo="${pid}" value="${preciosNuevos.get(pid) ?? ""}"
                     class="w-20 rounded-lg border border-amber-300 py-1 pl-5 pr-1 text-right text-sm outline-none focus:ring-2 focus:ring-masa-400/30" placeholder="0" />
            </span>`
          : `<span class="text-right text-sm tabular-nums text-masa-500">${sinPrecioLista ? "sin precio" : precioTxt}</span>`}
      </label>`;
    }

    function pintarLista(): void {
      const q = buscarEl.value.trim().toLowerCase();
      const filtrados = items.filter(
        (it) => !q || it.producto.descripcion.toLowerCase().includes(q) || String(it.producto.codigo).includes(q),
      );
      listaEl.innerHTML =
        filtrados.map(filaItem).join("") ||
        `<div class="px-3 py-3 text-sm text-masa-400">No hay artículos con ese nombre.</div>`;
    }
    pintarLista();

    buscarEl.addEventListener("input", pintarLista);
    listaEl.addEventListener("change", (ev) => {
      const t = ev.target as HTMLInputElement;
      if (t.matches("[data-chk]")) {
        const pid = Number(t.dataset.chk);
        if (t.checked) marcados.add(pid);
        else marcados.delete(pid);
        listaEl.querySelector(`[data-precio-wrap="${pid}"]`)?.classList.toggle("hidden", !t.checked);
      }
    });
    listaEl.addEventListener("input", (ev) => {
      const t = ev.target as HTMLInputElement;
      if (t.matches("[data-precio-nuevo]")) {
        const pid = Number(t.dataset.precioNuevo);
        const val = Math.max(0, Number(t.value) || 0);
        if (val > 0) preciosNuevos.set(pid, val);
        else preciosNuevos.delete(pid);
      }
    });

    dlgAdd.querySelector("#a_cancelar")?.addEventListener("click", () => dlgAdd.close());
    dlgAdd.querySelector("#a_aplicar")?.addEventListener("click", async () => {
      aErr.textContent = "";
      const chkLista = dlgAdd.querySelector("#a_lista_chk") as HTMLInputElement | null;
      const sumarLista = chkLista?.checked ?? false;
      const paraLista: Array<{ producto_id: number; precio: number }> = [];

      for (const it of items) {
        const pid = it.producto.id;
        const estaba = visible(it);
        const queda = marcados.has(pid);
        if (queda && !estaba) {
          // agregar al pedido
          st.ocultos.delete(pid);
          st.agregados.add(pid);
          const precioNuevo = preciosNuevos.get(pid) ?? 0;
          if (precioNuevo > 0) {
            st.overrides.set(pid, precioNuevo);
            if (sumarLista) paraLista.push({ producto_id: pid, precio: precioNuevo });
          }
        } else if (!queda && estaba) {
          // quitar del pedido
          st.ocultos.add(pid);
          st.cantidades.delete(pid);
          st.overrides.delete(pid);
          st.agregados.delete(pid);
        }
      }
      if (paraLista.length && st.prepara) {
        try {
          await api.put(`/api/listas/${st.prepara.lista.id}/precios`, { precios: paraLista });
        } catch (e) {
          aErr.textContent = "Aplicado al pedido, pero no se pudo guardar en la lista: " + (e instanceof Error ? e.message : "");
        }
      }
      localStorage.setItem(`pedido_cat_abierta_${catId}`, "1");
      dlgAdd.close();
      pintarGrilla();
    });

    dlgAdd.showModal();
  }

  async function cargarCliente(id: number, restaurar?: EstadoGuardado): Promise<void> {
    st.cantidades.clear();
    st.overrides.clear();
    st.ocultos.clear();
    st.agregados.clear();
    if (!id) {
      st.prepara = null;
      btnSugerir.disabled = true;
      pintarGrilla();
      guardarEstado();
      return;
    }
    grilla.innerHTML = `<div class="tarjeta text-masa-400">Cargando catálogo...</div>`;
    try {
      st.prepara = await api.get<PreparaPedido>(`/api/pedidos/preparar?cliente_id=${id}`);
      btnSugerir.disabled = false;
      if (restaurar?.empresa_id) selEmpresa.value = String(restaurar.empresa_id);
      if (restaurar) {
        for (const [k, v] of restaurar.cantidades) st.cantidades.set(k, v);
        for (const [k, v] of restaurar.overrides) st.overrides.set(k, v);
        for (const k of restaurar.ocultos) st.ocultos.add(k);
        for (const k of restaurar.agregados) st.agregados.add(k);
        inpFecha.value = restaurar.fecha || st.prepara.fecha_sugerida;
        inpDesc.value = String(restaurar.descuento ?? 0);
      } else {
        inpFecha.value = st.prepara.fecha_sugerida;
      }
      pintarGrilla();
    } catch (e) {
      grilla.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  selCliente.addEventListener("change", () => void cargarCliente(Number(selCliente.value)));
  inpFecha.addEventListener("change", guardarEstado);
  selEmpresa.addEventListener("change", guardarEstado);

  // Sugerir cantidades: precarga lo del ULTIMO pedido confirmado del cliente.
  btnSugerir.addEventListener("click", async () => {
    if (!st.prepara) return;
    if (st.cantidades.size > 0 && !confirm("Se reemplazan las cantidades ya cargadas. Continuar?")) return;
    try {
      const s = await api.get<SugeridoRespuesta>(`/api/pedidos/sugerido?cliente_id=${st.prepara.cliente.id}`);
      if (!s.pedido_id || s.lineas.length === 0) {
        alert("Este cliente no tiene pedidos anteriores para sugerir.");
        return;
      }
      st.cantidades.clear();
      const visiblesPorId = new Map(st.prepara.items.map((i) => [i.producto.id, i]));
      let cargadas = 0;
      for (const l of s.lineas) {
        const it = visiblesPorId.get(l.producto_id);
        if (!it || !visible(it)) continue; // solo productos con precio en la lista actual
        st.cantidades.set(l.producto_id, l.cantidad);
        localStorage.setItem(`pedido_cat_abierta_${it.producto.categoria_id}`, "1");
        cargadas++;
      }
      pintarGrilla();
      if (cargadas < s.lineas.length) {
        alert(`Se cargaron ${cargadas} de ${s.lineas.length} artículos (los demas ya no están en la lista del cliente).`);
      }
    } catch (e) {
      alert("No se pudo sugerir: " + (e instanceof Error ? e.message : "error"));
    }
  });

  grilla.addEventListener("input", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.matches("input.cant")) {
      const pid = Number(t.dataset.pid);
      // Acepta fracciones (2.5) y coma decimal (2,5).
      const val = Math.max(0, Number(t.value.replace(",", ".")) || 0);
      if (val > 0) st.cantidades.set(pid, val);
      else st.cantidades.delete(pid);
      actualizarSub(pid);
    } else if (t.matches("input.precio-input")) {
      const pid = Number(t.dataset.precio);
      const it = st.prepara?.items.find((i) => i.producto.id === pid);
      if (!it) return;
      const val = Math.max(0, Number(t.value) || 0);
      if (val === it.precio) st.overrides.delete(pid);
      else st.overrides.set(pid, val);
      const conOverride = st.overrides.has(pid);
      t.classList.toggle("precio-override", conOverride);
      grilla.querySelector(`[data-revert="${pid}"]`)?.classList.toggle("hidden", !conOverride);
      actualizarSub(pid);
    }
  });

  grilla.addEventListener("click", (ev) => {
    const addArt = (ev.target as HTMLElement).closest("[data-add-art]");
    if (addArt) { abrirArticulos(Number(addArt.getAttribute("data-add-art"))); return; }

    const sacar = (ev.target as HTMLElement).closest("[data-sacar]");
    if (sacar) {
      const pid = Number(sacar.getAttribute("data-sacar"));
      st.ocultos.add(pid);
      st.cantidades.delete(pid);
      st.overrides.delete(pid);
      st.agregados.delete(pid);
      pintarGrilla();
      return;
    }

    const step = (ev.target as HTMLElement).closest("[data-step]");
    if (step) {
      const pid = Number(step.getAttribute("data-pid"));
      const delta = Number(step.getAttribute("data-step"));
      const nuevo = Math.max(0, (st.cantidades.get(pid) ?? 0) + delta);
      if (nuevo > 0) st.cantidades.set(pid, nuevo);
      else st.cantidades.delete(pid);
      const inp = grilla.querySelector(`input.cant[data-pid="${pid}"]`) as HTMLInputElement | null;
      if (inp) inp.value = nuevo ? String(nuevo) : "";
      actualizarSub(pid);
      return;
    }

    const rev = (ev.target as HTMLElement).closest("[data-revert]");
    if (rev) {
      const pid = Number(rev.getAttribute("data-revert"));
      const it = st.prepara?.items.find((i) => i.producto.id === pid);
      st.overrides.delete(pid);
      const inp = grilla.querySelector(`input.precio-input[data-precio="${pid}"]`) as HTMLInputElement | null;
      if (inp && it) {
        inp.value = it.precio ? String(it.precio) : "";
        inp.classList.remove("precio-override");
      }
      rev.classList.add("hidden");
      actualizarSub(pid);
    }
  });

  inpDesc.addEventListener("input", recalcularTotal);

  contenedor.querySelector("#toggle")?.addEventListener("click", () => {
    st.verSoloCargados = !st.verSoloCargados;
    pintarGrilla();
  });
  contenedor.querySelector("#limpiar")?.addEventListener("click", () => {
    st.cantidades.clear();
    st.overrides.clear();
    st.ocultos.clear();
    st.agregados.clear();
    st.verSoloCargados = false;
    pintarGrilla();
  });

  contenedor.querySelector("#confirmar")?.addEventListener("click", async () => {
    if (!st.prepara) return;
    const lineas = [...st.cantidades.entries()]
      .filter(([, c]) => c > 0)
      .map(([producto_id, cantidad]) => {
        const linea: { producto_id: number; cantidad: number; precio_unitario?: number } = { producto_id, cantidad };
        if (st.overrides.has(producto_id)) linea.precio_unitario = st.overrides.get(producto_id);
        return linea;
      });
    if (lineas.length === 0) {
      alert("El pedido no tiene productos cargados.");
      return;
    }
    const empresaId = Number(selEmpresa.value);
    if (!empresaId) {
      alert("Elegí la empresa emisora de la comanda.");
      selEmpresa.focus();
      return;
    }
    const conCeros = lineas.some((l) => {
      const it = st.prepara?.items.find((i) => i.producto.id === l.producto_id);
      const precio = l.precio_unitario ?? it?.precio ?? 0;
      return precio <= 0;
    });
    if (conCeros && !confirm("Hay productos cargados sin precio (0). Confirmar igual?")) return;

    try {
      const r = await api.post<{ pedido: Pedido; lineas: PedidoLinea[] }>("/api/pedidos", {
        cliente_id: st.prepara.cliente.id,
        empresa_id: empresaId,
        fecha_entrega: inpFecha.value,
        turno,
        descuento: Number(inpDesc.value) || 0,
        lineas,
      });
      localStorage.removeItem(KEY_EN_CURSO); // pedido cerrado
      const empresa = empresas.find((e) => e.id === empresaId) ?? null;
      mostrarComanda(contenedor, st.prepara, r.pedido, r.lineas, empresa);
    } catch (e) {
      alert("No se pudo confirmar: " + (e instanceof Error ? e.message : "error"));
    }
  });

  // Restaurar pedido en curso desde el navegador (persiste al cambiar de pestana).
  const guardado = leerEstadoGuardado();
  if (guardado?.cliente_id) {
    selCliente.value = String(guardado.cliente_id);
    await cargarCliente(guardado.cliente_id, guardado);
  } else {
    inpFecha.value = "";
    // Con una sola opcion no hay nada que elegir: se pone sola. Hoy hay un
    // local y una empresa emisora, asi que eran dos clics obligatorios en cada
    // pedido (y olvidarse de la empresa hacia que el pedido se rechazara).
    // Va por cantidad y no por nombre: el dia que sumen otro local, el
    // selector vuelve a pedir que elijan.
    if (empresas.length === 1) selEmpresa.value = String(empresas[0].id);
    const unico = selCliente.options.length === 2 ? selCliente.options[1].value : "";
    if (unico) {
      selCliente.value = unico;
      await cargarCliente(Number(unico));
    }
  }
}

function mostrarComanda(
  contenedor: HTMLElement,
  prepara: PreparaPedido,
  pedido: Pedido,
  lineas: PedidoLinea[],
  empresa: Empresa | null,
): void {
  const cli = prepara.cliente;
  const nombre = new Map(prepara.items.map((i) => [i.producto.id, i.producto]));
  const filas = lineas
    .map((l) => {
      const p = nombre.get(l.producto_id);
      return `<tr class="border-t border-black/20">
        <td class="py-1 pr-2">${esc(p?.descripcion ?? l.producto_id)}</td>
        <td class="py-1 pr-2 text-right">${l.cantidad}</td>
        <td class="py-1 pr-2 text-right">${formatearPesos(l.precio_unitario)}</td>
        <td class="py-1 text-right">${formatearPesos(l.subtotal)}</td>
      </tr>`;
    })
    .join("");

  // Un remito (una via). Se imprimen dos: Via 1 (cliente) y Via 2 (control).
  const remito = (via: number): string => `
    <div class="mx-auto max-w-lg bg-white p-4 text-black ${via === 1 ? "print:break-after-page" : ""}">
      <div class="flex items-start justify-between">
        <div class="mb-2 flex items-center gap-3">
          ${marcaEmpresa()}
          <h2 class="text-xl font-bold">Remito</h2>
        </div>
        <span class="rounded border border-black/30 px-2 py-0.5 text-xs font-bold">VIA ${via} — ${via === 1 ? "CLIENTE" : "CONTROL"}</span>
      </div>
      ${empresa ? `<div class="border-b border-black/20 pb-1 text-xs">
        <b>Emite:</b> ${esc(empresa.nombre)}${empresa.direccion ? ` · ${esc(empresa.direccion)}` : ""}${empresa.telefono ? ` · Tel ${esc(empresa.telefono)}` : ""}
      </div>` : ""}
      <div class="mt-1 text-sm">
        <div><b>Cliente:</b> ${esc(cli.codigo)} — ${esc(cli.nombre)}</div>
        ${cli.direccion ? `<div><b>Direccion:</b> ${esc(cli.direccion)}</div>` : ""}
        <div><b>Entrega:</b> ${esc(pedido.fecha_entrega)}${pedido.turno ? ` — ${esc(nombreTurno(pedido.turno))}` : ""}</div>
        <div><b>Remito:</b> #${pedido.id}</div>
      </div>
      <table class="mt-3 w-full text-sm">
        <thead><tr class="text-left text-xs">
          <th>Producto</th><th class="text-right">Cant.</th><th class="text-right">P.Unit</th><th class="text-right">Subtotal</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
      <div class="mt-3 text-right text-lg font-bold">Total: ${formatearPesos(pedido.total)}</div>
      <div class="mt-6 flex justify-between text-xs text-black/60">
        <span>Firma: ____________________</span>
        <span>Aclaracion: ____________________</span>
      </div>
    </div>`;

  contenedor.innerHTML = `
    <div class="no-print mb-3 flex flex-wrap gap-2">
      <button id="imprimir" class="btn-primario">Imprimir remito (2 vias)</button>
      <button id="nuevo" class="btn-sec">Nuevo pedido</button>
      <span class="self-center text-sm text-green-700">✓ Pedido #${pedido.id} confirmado</span>
    </div>
    ${remito(1)}
    <div class="no-print my-3 border-t-2 border-dashed border-crema-300"></div>
    ${remito(2)}`;

  contenedor.querySelector("#imprimir")?.addEventListener("click", () => window.print());
  contenedor.querySelector("#nuevo")?.addEventListener("click", () => {
    void renderPedido(contenedor);
  });
}
